import { Alerta, Adjuntos, Badge, Button, Combobox, DataList, Desplegable, Dialog, MetricCard, MontoField, Row, Select, Spinner, Stack, TextField } from "@/components";
import type { Aporte, Cuenta } from "@/lib/api";
import { formatFecha, formatMoneda, formatNumero } from "@/lib/format";
import { parseMonto } from "@/lib/gastos";
import { VistaPreviaGrupo } from "@/tabs/grupos/VistaPreviaGrupo";
import { useAportesTab } from "./useAportesTab";

const columnasCuentas = [
  { clave: "nombre", titulo: "Fiduciante", render: (c: Cuenta) => c.nombre },
  { clave: "pedido", titulo: "Pedido", render: (c: Cuenta) => formatMoneda(c.pedido_usd, "USD") },
  { clave: "aportado", titulo: "Aportado", render: (c: Cuenta) => formatMoneda(c.aportado_usd, "USD") },
  {
    clave: "pendiente",
    titulo: "Pendiente",
    render: (c: Cuenta) => {
      const hayPendiente = parseMonto(c.pendiente_usd) !== null;
      const hayVencido = parseMonto(c.vencido_usd) !== null;
      return (
        <Row espacio={2}>
          {hayPendiente ? <Badge tono="alerta">{formatMoneda(c.pendiente_usd, "USD")}</Badge> : <span>{formatMoneda(c.pendiente_usd, "USD")}</span>}
          {hayVencido && <Badge tono="peligro">Vencido {formatMoneda(c.vencido_usd, "USD")}</Badge>}
        </Row>
      );
    },
  },
  { clave: "favor", titulo: "A favor", render: (c: Cuenta) => (parseMonto(c.a_favor_usd) !== null ? formatMoneda(c.a_favor_usd, "USD") : "—") },
];

export function AportesTab() {
  const t = useAportesTab();
  const d = t.dialogo;
  const listo = !t.cargando && !t.errorCarga;

  const columnasAportes = [
    { clave: "fecha", titulo: "Fecha", render: (a: Aporte) => formatFecha(a.fecha) },
    {
      clave: "nombre",
      titulo: "Fiduciante",
      render: (a: Aporte) =>
        a.grupo ? (
          <Row envolver espacio={2}>
            <span>{a.nombre}</span>
            <Badge tono="primario">{a.grupo}</Badge>
          </Row>
        ) : (
          a.nombre
        ),
    },
    { clave: "monto", titulo: "Monto", render: (a: Aporte) => formatMoneda(a.monto, a.moneda) },
    { clave: "tc", titulo: "Tipo de cambio", render: (a: Aporte) => (a.tipo_cambio ? formatNumero(a.tipo_cambio) : "—") },
    { clave: "usd", titulo: "En USD", render: (a: Aporte) => formatMoneda(a.monto_usd, "USD") },
    ...(t.cajaDe ? [{ clave: "caja", titulo: "Caja", render: t.cajaDe }] : []),
    { clave: "nota", titulo: "Nota", render: (a: Aporte) => a.nota ?? "—" },
  ];

  // Las partes del aporte de un grupo se corrigen desde Grupos (el backend responde 409 si no).
  const acciones = (a: Aporte) => !a.aporte_grupo_id && (
    <Row espacio={2}>
      <Button variante="secundario" tamanio="sm" aria-label={`Editar aporte del ${formatFecha(a.fecha)} de ${a.nombre}`} onClick={() => t.abrirEditar(a)}>
        Editar
      </Button>
      <Button variante="fantasma" tamanio="sm" aria-label={`Borrar aporte del ${formatFecha(a.fecha)} de ${a.nombre}`} onClick={() => t.abrirBorrar(a)}>
        Borrar
      </Button>
    </Row>
  );

  // El error de subir/borrar comprobantes se muestra una sola vez (arriba de "Aportes"), no repetido en cada Desplegable.
  const comprobantes = (lista: Aporte[]) =>
    lista.map((a) => (
      <Desplegable key={a.id} titulo={`Comprobantes del aporte del ${formatFecha(a.fecha)} de ${a.nombre} (${a.adjuntos.length})`}>
        <Adjuntos etiqueta={`aporte del ${formatFecha(a.fecha)} de ${a.nombre}`} adjuntos={a.adjuntos} rutaDescarga={t.rutaDescarga} puedeEditar={t.esAdmin} onSubir={t.subirA(a)} onBorrar={t.borrarAdjunto} subiendo={t.subiendo} error={null} />
      </Desplegable>
    ));

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Aportes</h1>
        {t.esAdmin && <Button onClick={t.abrirNuevo}>Registrar aporte</Button>}
      </Row>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}

      {listo && t.cuentas && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <MetricCard titulo="Pedido" valor={formatMoneda(t.cuentas.totales.pedido_usd, "USD")} />
            <MetricCard titulo="Aportado" valor={formatMoneda(t.cuentas.totales.aportado_usd, "USD")} />
            <MetricCard
              titulo="Pendiente"
              valor={formatMoneda(t.cuentas.totales.pendiente_usd, "USD")}
              detalle={
                parseMonto(t.cuentas.totales.vencido_usd) !== null ? (
                  <span className="text-peligro">Vencido: {formatMoneda(t.cuentas.totales.vencido_usd, "USD")}</span>
                ) : undefined
              }
            />
          </div>
          <DataList etiqueta="Cuentas por fiduciante" columnas={columnasCuentas} filas={t.cuentas.items} claveFila={(c) => c.fiduciante_id} vacio="Todavía no hay fiduciantes." />
          <Row envolver espacio={3} alinear="fin">
            <Select etiqueta="Fiduciante" opciones={t.opcionesFiltroFiduciante} valor={t.filtros.fiducianteId} onCambio={(v) => t.cambiarFiltro("fiducianteId", v)} className="min-w-48" />
            <TextField etiqueta="Desde" type="date" value={t.filtros.desde} onChange={(e) => t.cambiarFiltro("desde", e.target.value)} />
            <TextField etiqueta="Hasta" type="date" value={t.filtros.hasta} onChange={(e) => t.cambiarFiltro("hasta", e.target.value)} />
            {t.hayFiltros && (
              <Button variante="fantasma" onClick={t.limpiarFiltros}>
                Limpiar filtros
              </Button>
            )}
          </Row>
          <Alerta texto={t.errorAdjuntos} />
          {t.errorRecarga ? (
            <Stack espacio={3}>
              <Alerta texto={t.errorRecarga} />
              <div>
                <Button variante="secundario" onClick={t.reintentar}>
                  Reintentar
                </Button>
              </div>
            </Stack>
          ) : (
            <>
              <DataList etiqueta="Aportes" columnas={columnasAportes} filas={t.aportes} claveFila={(a) => a.id} acciones={t.esAdmin ? acciones : undefined} vacio="No hay aportes para mostrar." />
              {comprobantes(t.aportes)}
            </>
          )}
        </>
      )}

      <Dialog abierto={d?.tipo === "form"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={d?.tipo === "form" && d.aporte ? "Editar aporte" : "Registrar aporte"}>
        <form onSubmit={(e) => void t.guardar(e)} noValidate>
          <Stack espacio={4}>
            {t.opcionesAporta && <Select etiqueta="Aporta" opciones={t.opcionesAporta} valor={t.aporta} onCambio={t.cambiarAporta} />}
            {t.aporta === "grupo" ? (
              <Select etiqueta="Grupo" opciones={t.opcionesGrupo} valor={t.grupoId} onCambio={t.elegirGrupo} placeholder="Elegí el grupo" error={t.errores.grupo_id} />
            ) : (
            <Stack espacio={2}>
              <Combobox etiqueta="Fiduciante" opciones={t.opcionesFiduciante} onElegir={t.elegirFiduciante} placeholder="Escribí el nombre o tocá para ver la lista" />
              {t.nombreFiducianteForm && (
                <Row espacio={2}>
                  <Badge tono="primario" onQuitar={t.quitarFiduciante} etiquetaQuitar={`Quitar fiduciante ${t.nombreFiducianteForm}`}>
                    {t.nombreFiducianteForm}
                  </Badge>
                </Row>
              )}
              <Alerta texto={t.errores.fiduciante_id} />
            </Stack>
            )}
            <TextField etiqueta="Fecha" type="date" value={t.form.fecha} onChange={(e) => t.cambiar("fecha", e.target.value)} error={t.errores.fecha} />
            {t.opcionesCaja && <Select etiqueta="Caja" opciones={t.opcionesCaja} valor={t.form.caja_id} onCambio={(v) => t.cambiar("caja_id", v)} placeholder="Elegí la caja" />}
            <MontoField etiqueta="Monto" valor={t.form.monto} onCambio={(v) => t.cambiar("monto", v)} moneda={t.form.moneda} onCambioMoneda={(m) => t.cambiar("moneda", m)} error={t.errores.monto} />
            {t.form.moneda === "ARS" && (
              <TextField etiqueta="Tipo de cambio (ARS por USD)" inputMode="decimal" ayuda="Se sugiere el último usado" value={t.form.tipo_cambio} onChange={(e) => t.cambiar("tipo_cambio", e.target.value)} error={t.errores.tipo_cambio} />
            )}
            <TextField etiqueta="Nota" ayuda="Opcional" value={t.form.nota} onChange={(e) => t.cambiar("nota", e.target.value)} error={t.errores.nota} />
            <VistaPreviaGrupo partes={t.vistaPreviaGrupo} moneda={t.form.moneda} />
            <Alerta texto={t.errorGuardar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.guardando}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar aporte">
        {d?.tipo === "borrar" && (
          <Stack espacio={4}>
            <p>
              ¿Borrar el aporte del {formatFecha(d.aporte.fecha)} de {d.aporte.nombre} por {formatMoneda(d.aporte.monto, d.aporte.moneda)}?
              {d.aporte.adjuntos.length > 0 && ` Se borran también ${d.aporte.adjuntos.length} comprobante(s).`} No se puede deshacer.
            </p>
            <Alerta texto={t.errorBorrar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
                Borrar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
